<template lang="html">
  <v-layout column class="mb-4" v-if="opened">
    <PriorYearScheduleLineItemsDialog
      :opened.sync="priorYearScheduleLineItemsDialogOpened"
      :document="selectedPriorYearItem"
      :company-id="company.id"
    />

    <ImportPriorYearItemsDialog
      :opened.sync="importPriorYearItemsDialogOpened"
      :company="company"
      :year="year"
      @uploaded="importUploaded"
      :import-method="selectedImportMethod"
    />
    <v-layout
      class="ml-2"
      v-for="(item, i) in filteredPriorYearItems"
      :key="i"
      column
    >
      <v-layout align-center v-if="editingPriorYearItems.includes(item.id)">
        <div class="mr-1">•</div>
        <v-text-field
          v-model="item.name"
          class="default-input mr-2"
          hide-details
          solo
        />

        <v-btn
          color="primary"
          class="text-none mr-0"
          @click="updatePriorYearItem(item)"
        >
          Save
        </v-btn>

        <v-btn
          outline
          class="text-none error--text"
          @click="cancelEditPriorYearItem(item.id)"
        >
          Cancel
        </v-btn>
      </v-layout>

      <v-layout align-center class="mb-2 pa-2 prior-year-item" v-else>
        <div class="mr-3">
          {{ item.name }}
        </div>
        <v-tooltip top>
          <v-btn
            flat
            icon
            color="primary"
            @click="showPriorYearScheduleLineItems(item)"
            v-if="isBusinessOrRental(item.name)"
            class="ma-0"
            slot="activator"
          >
            <v-icon size="20">visibility</v-icon>
          </v-btn>

          <span>View line items</span>
        </v-tooltip>
        <v-spacer />
        <v-chip class="caption" small v-if="item.source">{{
          item.source
        }}</v-chip>
        <v-divider class="ma-1" vertical />
        <v-tooltip top>
          <v-btn
            flat
            icon
            color="primary"
            class="ma-0"
            slot="activator"
            @click="editPriorYearItem(item.id)"
          >
            <sx-icon :size="20" name="edit" />
          </v-btn>
          <span>Edit</span>
        </v-tooltip>

        <v-tooltip top>
          <v-btn
            flat
            icon
            class="ma-0"
            slot="activator"
            @click="deletePriorYearItem(item.id)"
          >
            <sx-icon :size="18" class="error--text" name="trash" />
          </v-btn>
          <span>Delete</span>
        </v-tooltip>
      </v-layout>
    </v-layout>

    <v-layout align-center v-if="addNewPriorYearItem === true">
      <div class="mr-1">•</div>
      <v-text-field
        v-model="newPriorYearItem.name"
        class="default-input mr-2"
        hide-details
        solo
      />

      <v-btn
        color="primary"
        class="text-none mr-0"
        @click="addPriorYearItem(newPriorYearItem)"
      >
        Save
      </v-btn>

      <v-btn outline class="text-none" @click="addNewPriorYearItem = false">
        Cancel
      </v-btn>
    </v-layout>

    <v-layout column v-else>
      <v-btn
        @click="addNewPriorYearItem = true"
        flat
        color="primary"
        class="text-capitalize"
      >
        <v-icon>add</v-icon> Add new item
      </v-btn>
    </v-layout>

    <v-layout column v-if="allowImport">
      <v-divider class="my-4" />
      <span class="mb-2 font-weight-bold">Import from PDF</span>
      <div class="mb-4">
        <v-alert :value="true" type="info" outline>
          Importing from a PDF will create roll-over items within a few minutes
          but accuracy may be low. Once you get a completion notification on the
          top right, please make sure to review the items imported. Please use
          <a
            href="https://soraban.notion.site/How-To-Export-Client-Data-From-Tax-Software-and-Upload-Into-Soraban-49d668522786472382b33621bc2944d8?pvs=4"
            >this guide</a
          >
          to export and upload organizers in bulk and review which items are
          supported. Only items uploaded via the Client Import Function should
          be accurate however will take longer.
          <span v-if="selectedImportMethod === 'ultratax_input_screens'"
            >Currently, there is a known issue with 1099-DIV's and 1099-INT's
            for UltraTax Input Screens, where company names may be split.</span
          >
        </v-alert>
      </div>
      <v-layout>
        <v-flex xs6>
          <v-select
            v-model="selectedImportMethod"
            placeholder="Select Import Method"
            class="default-input"
            :items="supportedImportMethods"
            item-text="name"
            item-value="value"
            solo
            hide-details
          />
        </v-flex>
        <v-flex xs6>
          <v-btn
            color="primary"
            class="my-0 text-capitalize"
            @click="openImportPriorYearItemsDialog"
            :disabled="!selectedImportMethod"
          >
            Upload for {{ year }}
          </v-btn>
        </v-flex>
      </v-layout>
    </v-layout>
  </v-layout>
</template>
<script>
import moment from 'moment';
import get from 'lodash/get';
import API from '@/api';
import { mapState, mapGetters } from 'vuex';
import { priorYearItemsImportMethods } from '@/constants/variables';
import isEmpty from 'lodash/isEmpty';
import PriorYearScheduleLineItemsDialog from '@/components/dialogs/PriorYearScheduleLineItemsDialog';
import ImportPriorYearItemsDialog from '@/components/dialogs/ImportPriorYearItemsDialog';
import ConfirmationDialog from '@/components/ConfirmationDialog';
import FirmPreferenceMixins from '../mixins/FirmPreferenceMixins';
import SxIcon from '@/components/sx/SxIcon';

export default {
  name: 'PriorYearItems',
  mixins: [FirmPreferenceMixins],
  props: {
    year: {
      type: Number,
    },
    allowImport: {
      required: false,
      type: Boolean,
      default: false,
    },
    company: {
      required: true,
    },
    opened: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    SxIcon,
    ImportPriorYearItemsDialog,
    PriorYearScheduleLineItemsDialog,
    ConfirmationDialog,
  },
  data() {
    return {
      selectedImportMethod: null,
      importPriorYearItemsDialogOpened: false,
      addNewPriorYearItem: false,
      selectedPriorYearItem: null,
      priorYearItems: [],
      editingPriorYearItems: [],
      newPriorYearItem: {
        name: '',
      },
      importTaxReturnTasks: [],
      priorYearItemsImportMethods,
      priorYearScheduleLineItemsDialogOpened: false,
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'user',
    }),

    supportedImportMethods() {
      if (this.allowTaxReturnImport) {
        return this.priorYearItemsImportMethods.filter(m => m.supported);
      } else {
        return this.priorYearItemsImportMethods
          .slice(1)
          .filter(m => m.supported);
      }
    },

    filteredPriorYearItems() {
      return this.priorYearItems.filter(i => i.year === this.year);
    },

    currentCompanyId() {
      if (this.company) {
        return this.company.id;
      } else {
        return null;
      }
    },
  },
  methods: {
    moment,
    isEmpty,
    get,

    isBusinessOrRental(name) {
      return [
        'schedule c',
        'schedule e',
        'business',
        'rental property',
        'rental activity',
      ].includes(name.toLowerCase().split(' (')[0]);
    },

    showPriorYearScheduleLineItems(doc) {
      this.selectedPriorYearItem = doc;
      this.priorYearScheduleLineItemsDialogOpened = true;
    },

    async getImportTaxReturnTasks() {
      const response = await API.extractedDocument.getImportTaxReturnTasks({
        companyId: this.currentCompanyId,
        year: this.year,
      });

      this.importTaxReturnTasks = response.data;
    },

    async downloadTaxReturn(task) {
      const response = await API.advisor.downloadDocument({
        link: task.extracted_document.source,
      });

      if (response.data.document) {
        window.open(response.data.document.data.link, '_blank');
      }
    },
    async markImportTaskAsCompleted(task) {
      const response = await API.extractedDocument.updateImportTaxReturnTask({
        companyId: this.currentCompanyId,
        taskId: task.id,
        status: 'completed',
      });

      await this.getImportTaxReturnTasks();
    },
    async restoreImportTask(task) {
      const response = await API.extractedDocument.updateImportTaxReturnTask({
        companyId: this.currentCompanyId,
        taskId: task.id,
        status: 'review_required',
      });

      await this.getImportTaxReturnTasks();
    },
    async deleteImportTask(task) {
      const response = await API.extractedDocument.deleteImportTaxReturnTask({
        companyId: this.currentCompanyId,
        taskId: task.id,
      });

      await this.getImportTaxReturnTasks();
    },

    async importUploaded() {
      if (this.selectedImportMethod === 'tax_return') {
        await this.getImportTaxReturnTasks();
      } else {
        this.$emit('close-dialog');
      }
      this.$store.dispatch('setCurrentFirm');
    },

    async loadPriorYearItems() {
      if (this.currentCompanyId) {
        console.log('refreshed');
        try {
          const response = await API.priorYearItem.getPriorYearItems({
            companyId: this.currentCompanyId,
          });
          this.priorYearItems = response.data;
          this.selectedImportMethod = this.savedTaxOrganizer;
        } catch (e) {
          console.log(e);
        }
      }
    },

    openImportPriorYearItemsDialog() {
      this.importPriorYearItemsDialogOpened = true;
    },

    editPriorYearItem(itemId) {
      this.editingPriorYearItems.push(itemId);
    },
    async deletePriorYearItem(itemId) {
      try {
        const response = await API.priorYearItem.deletePriorYearItem({
          priorYearItemId: itemId,
          companyId: this.currentCompanyId,
        });
        this.loadPriorYearItems();

        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Deleted successfully',
        });
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Error occured. Please try again later.',
        });
      }
    },
    cancelEditPriorYearItem(itemId) {
      this.editingPriorYearItems = this.editingPriorYearItems.filter(
        currentItemId => currentItemId !== itemId,
      );
    },
    async addPriorYearItem() {
      try {
        const response = await API.priorYearItem.addPriorYearItem({
          name: this.newPriorYearItem.name,
          year: this.year,
          companyId: this.currentCompanyId,
        });

        this.addNewPriorYearItem = false;
        this.newPriorYearItem = {
          name: '',
        };

        this.loadPriorYearItems();
        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Added new rollover item successfully.',
        });
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Error occured. Please try again later.',
        });
      }
    },
    async updatePriorYearItem(item) {
      try {
        const response = await API.priorYearItem.updatePriorYearItem({
          priorYearItemId: item.id,
          name: item.name,
          year: item.year,
          companyId: this.currentCompanyId,
        });
        this.cancelEditPriorYearItem(item.id);

        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Successfully updated!',
        });
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Error occured. Please try again later.',
        });
      }
    },
  },
  watch: {
    currentCompanyId: {
      handler(value) {
        if (value) {
          this.loadPriorYearItems();
          if (this.allowTaxReturnImport) {
            this.getImportTaxReturnTasks();
          }
        } else {
          this.priorYearItems = [];
        }
      },
    },
    opened: {
      handler(value) {
        if (value) {
          this.loadPriorYearItems();
          if (this.allowTaxReturnImport) {
            this.getImportTaxReturnTasks();
          }
        } else {
          this.priorYearItems = [];
        }
      },
    },
    year: {
      handler(value) {
        if (value) {
          this.loadPriorYearItems();
          if (this.allowTaxReturnImport) {
            this.getImportTaxReturnTasks();
          }
        } else {
          this.priorYearItems = [];
        }
      },
    },
  },
};
</script>

<style lang="postcss" scoped>
.prior-year-item {
  border: 1px solid #d6d6d9;
  border-radius: 8px;
}
</style>
